//old import { useCallback, useEffect, useState } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
//old import { Briefcase, MapPin, Search } from "lucide-react";
import { ArrowRight, Briefcase, MapPin, Search } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import api from "../api/api";
import { useAuth } from "../hooks/useAuth";
import { isAuthenticatedUser } from "../utils/authUser";

/*
 * Job preview for visitors (/jobs): what the official website's Job Search page
 * shows ("Preview our job links and sign up for details", Alicia). Active jobs
 * by title and location, without the employer's link: visitors sign up to see
 * a job's details, apply and track it. It works on its own or inside the
 * website's page (an iframe), so its links open in the whole window (_top).
 */

const formatPosted = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const inputCls = "w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-3 text-slate-900 placeholder-slate-400";

export default function PublicJobs() {
    const { user } = useAuth();
    const signedIn = isAuthenticatedUser(user);
    //old const [form, setForm] = useState({ search: "", location: "" });
    //old const [query, setQuery] = useState({ search: "", location: "", page: 1 });
    // the search is in the address (?search=&location=&page=), so Back brings it back
    const [searchParams, setSearchParams] = useSearchParams();
    const query = useMemo(() => ({
        search: searchParams.get("search") || "",
        location: searchParams.get("location") || "",
        page: Math.max(parseInt(searchParams.get("page"), 10) || 1, 1),
    }), [searchParams]);
    const [form, setForm] = useState({ search: query.search, location: query.location });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: query.page };
            if (query.search) params.search = query.search;
            if (query.location) params.location = query.location;
            const res = await api.get("/jobs/public", { params });
            setData(res.data);
            setError("");
        } catch (err) {
            setError(err.response?.status === 429
                ? "Too many searches at once. Wait a minute and try again."
                : "We couldn't load jobs right now. Try again in a moment.");
        } finally {
            setLoading(false);
        }
    }, [query]);

    useEffect(() => { load(); }, [load]);
    // Back and Forward put their search back in the boxes too
    useEffect(() => {
        setForm({ search: query.search, location: query.location });
    }, [query.search, query.location]);
    // a page past the last one (an old link, or fewer jobs since) opens the last page
    // instead, in place of this address so Back doesn't come back to it
    useEffect(() => {
        if (!data || !data.total || data.jobs?.length || data.page <= data.totalPages) return;
        const next = new URLSearchParams(searchParams);
        if (data.totalPages > 1) next.set("page", data.totalPages);
        else next.delete("page");
        if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    }, [data, searchParams, setSearchParams]);

    // Search is debounced (Evan, Oct 21): clicks within half a second of the last search are ignored, so ten
    // quick clicks send one request
    const lastSearchAt = useRef(0);
    const submit = (e) => {
        e.preventDefault();
        if (Date.now() - lastSearchAt.current < 500) return;
        lastSearchAt.current = Date.now();
        //old setQuery({ search: form.search.trim(), location: form.location.trim(), page: 1 });
        const next = {};
        if (form.search.trim()) next.search = form.search.trim();
        if (form.location.trim()) next.location = form.location.trim();
        // the same search again just reloads it (no new address for Back to step through)
        if (new URLSearchParams(next).toString() === searchParams.toString()) load();
        else setSearchParams(next);
    };
    const goPage = (page) => {
        //old setQuery((q) => ({ ...q, page }));
        const next = new URLSearchParams(searchParams);
        if (page > 1) next.set("page", page);
        else next.delete("page");
        setSearchParams(next);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const jobs = data?.jobs || [];
    // there are jobs, just not on this page: the last page is on its way
    const pastLastPage = !!data && data.total > 0 && jobs.length === 0 && data.page > data.totalPages;
    const first = data && data.total ? (data.page - 1) * 20 + 1 : 0;
    const last = data ? first + jobs.length - 1 : 0;
    // signed-in users go to the full Job Browser; visitors to sign up
    const cta = signedIn
        ? { href: "/jobbrowser", label: "Open in Job Browser" }
        : { href: "/signup", label: "Sign up to see details" };
    // signed in, the Job Browser opens on that job (like the dashboard's recommended jobs);
    // inside the website's page (an iframe) the link still opens the whole window
    const inWebsite = window.self !== window.top;

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900">
            <main className="mx-auto max-w-5xl px-4 py-8 sm:py-12">
                <h1 className="text-3xl font-extrabold">Find jobs</h1>
                <p className="mt-2 max-w-2xl text-slate-500">
                    {/* old Preview open jobs. Sign up free to see each job's details, apply, and keep track of your */}
                    {/* old applications. */}
                    {signedIn
                        ? "Preview open jobs. Open one in the Job Browser to see its details and apply."
                        : "Preview open jobs. Sign up free to see each job's details, apply, and keep track of your applications."}
                </p>

                <form onSubmit={submit} className="mt-6 grid gap-3 sm:grid-cols-[1fr_1fr_auto]" role="search">
                    <label className="relative block">
                        <span className="sr-only">Job title</span>
                        <Briefcase size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input className={inputCls} placeholder="Job title" maxLength={100} value={form.search}
                               onChange={(e) => setForm((f) => ({ ...f, search: e.target.value }))} />
                    </label>
                    <label className="relative block">
                        <span className="sr-only">City or state</span>
                        <MapPin size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input className={inputCls} placeholder="City or state" maxLength={100} value={form.location}
                               onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} />
                    </label>
                    <button type="submit" className="flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-primary-700">
                        <Search size={18} /> Search
                    </button>
                </form>

                {error && <p role="alert" className="mt-6 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}

                <div className="mt-6" aria-live="polite">
                    {/* old {loading && !data ? ( */}
                    {(loading && !data) || pastLastPage ? (
                        <div className="grid gap-4 md:grid-cols-2">
                            {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={140} borderRadius={16} />)}
                        </div>
                    ) : data && jobs.length === 0 ? (
                        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
                            <p className="font-bold">No jobs match that search</p>
                            <p className="mt-1 text-sm text-slate-500">Try another job title or location.</p>
                        </div>
                    ) : data && (
                        <>
                            <p className="mb-3 text-sm text-slate-500 tabular-nums">
                                Showing {first.toLocaleString()}–{last.toLocaleString()} of {data.total.toLocaleString()} jobs
                            </p>
                            <ul className={`grid gap-4 md:grid-cols-2 ${loading ? "opacity-60" : ""}`}>
                                {jobs.map((j) => (
                                    <li key={j.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                        {/* old <h2 className="break-words text-lg font-bold">{j.title}</h2> */}
                                        {/* a small arrow on the title's row instead of the button under it (Alicia, Oct 30: a compact arrow), the button's words as its tooltip and name */}
                                        <div className="flex items-start justify-between gap-3">
                                            <h2 className="min-w-0 break-words text-lg font-bold">{j.title}</h2>
                                            <Link to={cta.href} target={signedIn && !inWebsite ? undefined : "_top"}
                                                  state={signedIn ? { openJobId: j.id, openJobTitle: j.title || "" } : undefined}
                                                  title={cta.label} aria-label={cta.label}
                                                  className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-primary-200 bg-primary-50 text-primary-700 transition-colors hover:bg-primary-100">
                                                <ArrowRight size={18} aria-hidden="true" />
                                            </Link>
                                        </div>
                                        {/* old <p className="mt-1 break-words font-medium text-slate-700">{j.company}</p> */}
                                        {/* title, place and date only (Sep 4): visitors sign up to see who's hiring */}
                                        <p className="mt-1 text-sm text-slate-500">
                                            {/* old {[j.location, j.type].filter(Boolean).join(" · ")} */}
                                            {/* old {j.postedOn && <> · Posted {formatPosted(j.postedOn)}</>} */}
                                            {[j.location, j.postedOn && `Posted ${formatPosted(j.postedOn)}`].filter(Boolean).join(" · ")}
                                        </p>
                                        {/* old <div className="mt-4 flex-1" /> */}
                                        {/* old <a href={cta.href} target="_top" */}
                                        {/* old <Link to={cta.href} target={signedIn && !inWebsite ? undefined : "_top"} */}
                                        {/* old       state={signedIn ? { openJobId: j.id, openJobTitle: j.title || "" } : undefined} */}
                                        {/* old    className="inline-flex items-center justify-center rounded-xl border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"> */}
                                        {/* old     {cta.label} */}
                                        {/* old </a> */}
                                        {/* old </Link> */}
                                    </li>
                                ))}
                            </ul>
                            {data.totalPages > 1 && (
                                <nav aria-label="Pages" className="mt-6 flex items-center justify-center gap-3">
                                    <button type="button" disabled={data.page <= 1 || loading} onClick={() => goPage(data.page - 1)}
                                            className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-40">Previous</button>
                                    <span className="text-sm text-slate-500 tabular-nums">Page {data.page} of {data.totalPages}</span>
                                    <button type="button" disabled={data.page >= data.totalPages || loading} onClick={() => goPage(data.page + 1)}
                                            className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-40">Next</button>
                                </nav>
                            )}
                        </>
                    )}
                </div>

                {!signedIn && (
                    <p className="mt-8 text-center text-sm text-slate-600">
                        Already have an account? <a href="/login" target="_top" className="font-semibold text-primary-600 hover:text-primary-700">Sign in</a>
                    </p>
                )}
            </main>
        </div>
    );
}
